<template>
  <el-container class="feed-card">
    <el-main class="shadow">
      <el-card>
        <template #header>
          <div class="header-container">
            <div class="feed-title">
              <span class="feed-id"> {{ feed.id }}. </span>
              <span class="feed-name">
                {{ feed.title }}
              </span>
              <FeedState class="ml-1" :state="feed.feed_state" />
              <StreamIcon class="stream-icon" :stream-id="feed.stream" />
              <AdTypeLabel
                v-for="typeId in feed.type_ids"
                :key="typeId"
                :type="typeId"
              />
              <ProtocolLabel :key="feed.type" :type="feed.type" />
              <DeviceIcon
                v-for="id in feed.device_ids"
                :key="id"
                :device-id="id"
                class="ml-1"
              />
              <ViolationsButton
                :aggressive="feed.aggressive"
                @click="showMalwareModal"
              />
            </div>
            <FeedActions :feed="feed" :show-delete="isStaff" />
          </div>
        </template>
      </el-card>
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, computed, inject } from 'vue'
import { useFeedsStore } from '@/store/modules/feeds'
import { AdType } from '@/api/types'

import StreamIcon from '@/components/StreamIcon.vue'
import FeedActions from './FeedActions.vue'
import ProtocolLabel from '../../components/ProtocolLabel.vue'
import DeviceIcon from '@/apps/traforama/components/DeviceIcon.vue'
import FeedState from '@/apps/traforama/views/integrations/components/FeedState.vue'
import AdTypeLabel from '@/apps/traforama/components/AdTypeLabel.vue'
import ViolationsButton from '@/apps/traforama/components/ViolationsButton.vue'
import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'FeedRow',
  components: {
    FeedActions,
    StreamIcon,
    ProtocolLabel,
    FeedState,
    DeviceIcon,
    AdTypeLabel,
    ViolationsButton
  },
  props: {
    feedId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const feedsStore = useFeedsStore()
    const userStore = useUserStore()

    const { isStaff } = userStore
    const feed = computed(() => {
      const feed = feedsStore.items[props.feedId]
      return feed?.attributes
    })

    const openMalwareModal = inject('openMalwareModal')

    const showMalwareModal = () => {
      if (openMalwareModal) {
        openMalwareModal({ id: props.feedId, name: feed.value.name })
      }
    }

    return { feed, AdType, isStaff, closed, showMalwareModal }
  }
})
</script>

<style lang="scss" scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .mr-1 {
    margin-right: 1em;
  }

  &.mobile {
    flex-direction: column;
    align-items: flex-start;
  }
}

.feed-id {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.feed-title {
  margin-left: 10px;
}

.stream-icon {
  margin-left: 0.5em;
  color: var(--el-text-color-secondary);
}

.feed-card {
  margin-bottom: 10px;

  :deep(.el-card__header) {
    padding: 5px 20px 5px 0;

    arrow {
      margin: 5px;
    }
  }

  :deep(.el-card) {
    &:hover {
      box-shadow: -4px 4px 9px -1px #a3d3ff;
    }
  }

  :deep(.el-card__body) {
    padding: 0 5px;
  }

  .el-main {
    padding: 2px 20px;
  }

  :deep(.creatives-list) {
    transition: height 1s ease-out;
    opacity: 0;
    height: 0;
    overflow: hidden;
  }

  :deep(.creatives-list.active) {
    opacity: 1;
    height: auto;
    transition: height 1s;
  }

  :deep(.feed-name) {
    font-weight: 600;
    font-size: 14px;
  }

  .empty {
    padding: 5px;
    color: var(--el-text-color-secondary);
  }
}
</style>
